import React, { useContext, useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';
import Grid from '@mui/material/Grid';
import { PlaceStoreContext } from '../../../reducers/Place/PlaceContext';
import { AuthContext } from '../../../reducers/Auth/AuthContext';
import { EditField } from '../../../components/EditField';
import PDFExporter from '../../../components/PDFExporter';
import BarLoader from 'react-spinners/BarLoader';
import { EditImage } from '../../../components/EditImage';
import { updatePlaceInfo } from '../../../apis/updatePlaceInfo';
import { updatePlaceImage } from '../../../apis/updatePlaceImage';
import config from '../../../utils/config';
import PublishButton from '../../../components/PublishButton';
import { EditYesNo } from '../../../components/EditYesNo';
import { ViewsCurrentYear } from './graph/ViewsCurrentYear';
import ReactModal from '../../../components/ReactModal';

const DeliveryHours = (props) => {
  const { day, place, type, active } = props;
  const availability =
    place?.features?.delivery?.[type]?.availability?.[day[1]]?.replace('-', ' - ') || 'N/A';
  return (
    <p className={`f3b f08 mb-4${active ? ' txt-info' : ' txt-disabled-more'}`}>
      <span className="f3 f08">{`${day[0]}: `}</span>
      {availability}
    </p>
  );
};

const TabPlace = () => {
  const [placeState] = useContext(PlaceStoreContext);
  const [authState] = useContext(AuthContext);
  const initalFile = null;
  const [file, setFile] = useState(initalFile);
  const [initialData, setInitialData] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [exportToPDF, setExportToPDF] = useState(false);

  const { place } = placeState;
  const {
    user: { placeId, role },
  } = authState;

  const isUnitpay = role === 'unitpay';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const imageUrl = place.has_image ? `${config.uri.bucket}/${placeId}/location.jpg?time=${new Date().getTime()}` : null;

    const imageDetails = {
      image: imageUrl,
      local: false,
    };

    const initialValues = {
      image: imageDetails,
      name: place.name || '',
      address: place.address || '',
      latitude: place.latitude || '',
      longitude: place.longitude || '',
      email: place.email || '',
      mobile: place.mobile || '',
      facebook: place.facebook || '',
      instagram: place.instagram || '',
      google: place.google || '',
      tripadvisor: place.tripadvisor || '',
      bookingham: place.bookingham || '',
      deliveryActive: Boolean(place?.features?.delivery?.active) || false,
    };

    setInitialData({
      initialValues,
      imageUrl: imageDetails.image,
    });
  }, [placeState]);

  if (initialData === null) {
    return null;
  }

  return (
    <>
      <div style={{ marginTop: '40px' }}>
        <div style={{ height: 320, marginLeft: 16, marginRight: 16 }}>
          <ViewsCurrentYear placeId={placeId} />
        </div>
        <Formik
          enableReinitialize
          initialValues={initialData.initialValues}
          validationSchema={Yup.object({
            name: Yup.string()
              .max(64, 'Must be 64 characters or less')
              .test(
                'value',
                'Must have at least one character',
                (value) => value && value.trim().length >= 1,
              ),
            address: Yup.string()
              .max(128, 'Must be 128 characters or less')
              .test(
                'value',
                'Must have at least one character',
                (value) => value && value.trim().length >= 1,
              ),
            latitude: Yup.string()
              .nullable() // Allows null values, so no validation if field is empty
              .matches(
                /^(-?\d+(\.\d{0,6})?|\.?\d{1,6})$/,
                'Must not be between -90 and 90 with an accuracy of max 6 digits',
              )
              .test(
                'value',
                'Must have at least one character',
                (value) => !value || value.trim().length >= 1 // Only validate if value is present
              )
              .test(
                'value',
                'Must be between -90 and 90',
                (value) => !value || (Number(value) >= -90 && Number(value) <= 90) // Only validate if value is present
              ),
              longitude: Yup.string()
              .nullable() // Allows null values, so no validation if the field is empty
              .matches(
                /^(-?\d+(\.\d{0,6})?|\.?\d{1,6})$/,
                'Must not be between -180 and 180 with an accuracy of max 6 digits',
              )
              .test(
                'value',
                'Must have at least one character',
                (value) => !value || value.trim().length >= 1 // Only validate if value is present
              )
              .test(
                'value',
                'Must be between -180 and 180',
                (value) => !value || (Number(value) >= -180 && Number(value) <= 180) // Only validate if value is present
              ),
            email: Yup.string().email(),
            mobile: Yup.string().max(32),
            facebook: Yup.string().url(),
            instagram: Yup.string().url(),
            google: Yup.string().url(),
            tripadvisor: Yup.string().url(),
            bookingham: Yup.string().url(),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            if (!placeId) return;
            setSubmitting(true);

            let hasImage = false;

            if (file) {
              hasImage = true;
              await updatePlaceImage(placeId, file);
            }

            if (removeImage) {
              values.hasImage = false;
            } else {
              values.hasImage = place.has_image ? place.has_image : hasImage;
            }

            await updatePlaceInfo(placeId, values);

            if (file) {
              setFile(null);
            }

            setSubmitting(false);
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => {
            if (isSubmitting)
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100vw',
                    height: '70vh',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <p style={{ margin: 0, paddingBottom: '20px', fontWeight: 'bold' }}>
                      SAVING CHANGES AND PUBLISHING ...
                    </p>
                    <BarLoader height={4} width={100} color="#000000" loading={true} />
                  </div>
                </div>
              );

            const submitDisabled = file === null && _.isEqual(initialData.initialValues, values);

            return (
              <Form encType="multipart/form-data" style={{ margin: '16px', padding: 0 }}>
                <Grid container justify="center">
                  <Grid item xs={12} sm={12} md={6} style={{ marginBottom: '16px' }}>
                    <PublishButton
                      name="SAVE CHANGES AND PUBLISH"
                      disabled={submitDisabled}
                      style={{ marginLeft: '8px' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} style={{ marginBottom: '16px' }}>
                    <div className="f-c f-aic f-jcsb">
                      <div style={{ fontSize: '0.8rem', marginLeft: '8px' }}>
                        ID: {placeId} - URL:{' '}
                        <a
                          href={
                            config.uri.name === 'production'
                              ? `https://digital-menu.${process.env.REACT_APP_DOMAIN}/${placeId}`
                              : `https://staging.digital-menu.${process.env.REACT_APP_DOMAIN}/${placeId}`
                          }
                          target="_blank"
                          rel="noreferrer"
                        >{`https://digital-menu.${process.env.REACT_APP_DOMAIN}/${placeId}`}</a>
                      </div>
                      <div className="ml-8">
                        <Button
                          variant="contained"
                          color="secondary"
                          component="span"
                          onClick={() => {
                            setExportToPDF(true);
                          }}
                        >
                          export pdf (beta)
                        </Button>
                      </div>
                    </div>
                  </Grid>
                  <Grid item container spacing={2} xs={12}>
                    <Grid item xs={12} sm={12} md={6}>
                      <EditImage
                        initialImage={setInitialData.imiageUrl}
                        image={values.image}
                        removeImage={removeImage}
                        setRemoveImage={setRemoveImage}
                        setFieldValue={setFieldValue}
                        setFile={setFile}
                        file={file}
                        height="412px"
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <EditField
                        required
                        label="Place Name"
                        name="name"
                        style={{ paddingBottom: '15px' }}
                      />
                      <EditField label="Address" name="address" style={{ paddingBottom: '15px' }} />
                      <EditField
                        label="Latitude"
                        name="latitude"
                        style={{ paddingBottom: '15px' }}
                      />
                      <EditField
                        label="Longitude"
                        name="longitude"
                        style={{ paddingBottom: '15px' }}
                      />
                      <EditField label="Email" name="email" style={{ paddingBottom: '15px' }} />
                      <EditField label="Mobile" name="mobile" style={{ paddingBottom: '15px' }} />
                    </Grid>
                  </Grid>
                  <Grid item container spacing={2} xs={12}>
                    <Grid item xs={12} sm={12} md={6}>
                      <EditField
                        label="Facebook"
                        name="facebook"
                        style={{ paddingBottom: '15px' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <EditField
                        label="Instagram"
                        name="instagram"
                        style={{ paddingBottom: '15px' }}
                      />
                    </Grid>
                  </Grid>
                  <Grid item container spacing={2} xs={12}>
                    <Grid item xs={12} sm={12} md={6}>
                      <EditField label="Google" name="google" style={{ paddingBottom: '15px' }} />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <EditField
                        label="Tripadvisor"
                        name="tripadvisor"
                        style={{ paddingBottom: '15px' }}
                      />
                    </Grid>
                    {isUnitpay && <Grid item xs={12} sm={12} md={6}>
                      <EditField
                        label="Bookingham"
                        name="bookingham"
                        style={{ paddingBottom: '15px' }}
                      />
                    </Grid>}
                  </Grid>
                  {place.has_delivery && (
                    <Grid item container spacing={2} xs={12}>
                      <Grid item xs={12}>
                        <h4>Delivery Options</h4>
                      </Grid>
                      <Grid item xs={12}>
                        <EditYesNo label="Delivery Active" name="deliveryActive" />
                      </Grid>
                      <Grid item xs={12}>
                        <div className="f-c mb-32">
                          <div>
                            <h5>Delivery Hours Available</h5>
                            <div className="f-c f-dirc">
                              <DeliveryHours
                                day={['Monday', 'mon']}
                                place={place}
                                type="delivery"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Tuesday', 'tue']}
                                place={place}
                                type="delivery"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Wednesday', 'wed']}
                                place={place}
                                type="delivery"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Thursday', 'thu']}
                                place={place}
                                type="delivery"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Friday', 'fri']}
                                place={place}
                                type="delivery"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Saturday', 'sat']}
                                place={place}
                                type="delivery"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Sunday', 'sun']}
                                place={place}
                                type="delivery"
                                active={values.deliveryActive}
                              />
                            </div>
                          </div>
                          <div className="ml-16">
                            <h5>Pickup Hours Available</h5>
                            <div className="f-c f-dirc">
                              <DeliveryHours
                                day={['Monday', 'mon']}
                                place={place}
                                type="pickup"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Tuesday', 'tue']}
                                place={place}
                                type="pickup"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Wednesday', 'wed']}
                                place={place}
                                type="pickup"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Thursday', 'thu']}
                                place={place}
                                type="pickup"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Friday', 'fri']}
                                place={place}
                                type="pickup"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Saturday', 'sat']}
                                place={place}
                                type="pickup"
                                active={values.deliveryActive}
                              />
                              <DeliveryHours
                                day={['Sunday', 'sun']}
                                place={place}
                                type="pickup"
                                active={values.deliveryActive}
                              />
                            </div>
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </div>
      {exportToPDF && (
        <ReactModal modalIsOpen={exportToPDF} setIsOpen={setExportToPDF} fullpage>
          <PDFExporter />
        </ReactModal>
      )}
    </>
  );
};

export default TabPlace;
